import useNotification from "component/global/event-handler/hooks/use-notification";
import useCodeScriptFunctionInput from "component/global/generic-hooks/use-code-script-fn-api";
import { useFormContext } from "react-hook-form";
import { useContext, type ContextType } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { executeActionScript } from "modules/record/record-producer/services/compileRecordProducerActionScript";
import { emitRecordMutation } from "shared/services/recordMutationBus";
import { useSession } from "app/hooks/useSession";
import SysRecord from "app/class/record";
import { RecordProducerContext } from "../components/RecordProducerContext";
import { sanitizePasswordPayloadValues } from "modules/record/shared/services/passwordField";
import { navigateToReturnTarget } from "modules/record/shared/services/recordNavigationReturn";
import { formValuesUnchanged, normalizeCodePayload } from "modules/record/shared/services/recordQaBehavior";
import { focusFirstInvalidField } from "../services/focusFirstInvalidField";

const shouldRespectFormValidation = (action?: SysRecord | null) =>
    Boolean(
        action?.respect_form_validation ??
        action?.respect_validation ??
        action?.validate_form_before_run
    );

type SuccessNavigation = "custom" | "stay" | "return_to" | "current_record" | "table_list";
type SuccessNavigationFallback = "none" | "current_record" | "table_list";

const useRecordProducerActionCompiler = () => {
    const { api } = useCodeScriptFunctionInput();
    const navigate = useNavigate();
    const notifyApi = useNotification();
    const { data: userSession } = useSession();
    const recordProducer = useContext(RecordProducerContext);
    const stayOnPageByContext = recordProducer?.embedded === true;

    const { getValues, trigger, control, reset } = useFormContext();

    const actionContext = {
        ...api,
        ...notifyApi,
        currentUser: userSession,
        navigate
    };

    const runActionScript = async (action: SysRecord, tableName: string, mode: "form" | "related_form" = "form", stayOnPage = false): Promise<boolean> => {
        try {
            const flushed = await recordProducer?.flushPendingTranslations?.();
            if (flushed === false) {
                notifyApi.warning(
                    "RECORD-PRODUCER-TRANSLATION-PENDING",
                    "Confirm or discard pending translations before running this action."
                );
                return false;
            }
            if (recordProducer?.mode === "edit" && formValuesUnchanged(getValues(), (control as { _defaultValues?: unknown } | undefined)?._defaultValues) &&
                ["Save", "Update"].includes(action.label) &&
                ["stay", "return_to"].includes(action.success_navigation)) {
                notifyApi.info("RECORD-PRODUCER-NO-CHANGES", "No changes to save.");
                if (!stayOnPage && !stayOnPageByContext) handleSuccessNavigation({ action, tableName, navigate, currentValues: getValues(), returnValue: null });
                return true;
            }
            if (shouldRespectFormValidation(action)) {
                const isValid = await trigger(undefined, { shouldFocus: true });

                if (!isValid) {
                    setTimeout(focusFirstInvalidField, 0);
                    notifyApi.warning(
                        "RECORD-PRODUCER-ACTION-VALIDATION",
                        "Please fix the validation errors before running this action."
                    );
                    return false;
                }

                const canSubmit = await recordProducer?.runClientScripts?.("onSubmit");

                if (canSubmit === false) {
                    return false;
                }
            }

            const result = await executeActionScript(
                action.script,
                actionContext,
                buildCurrentPayload(getValues(), recordProducer),
                tableName,
                mode,
                action.timeout_ms
            );

            if (!result?.mutatedTables.length) {
                emitRecordMutation({
                    table: tableName,
                    source: "record_producer_action",
                });
            }

            if (result?.mutatedTables.includes(tableName) || ["Save", "Update"].includes(action.label)) {
                reset(getValues(), { keepDefaultValues: false });
            }
            if (!stayOnPage && !stayOnPageByContext) handleSuccessNavigation({
                action,
                tableName,
                navigate,
                currentValues: getValues(),
                returnValue: result?.returnValue,
            });
            return true;
        } catch (error: any) {
            notifyApi.alert(
                "RECORD-PRODUCER-ACTION-ERROR",
                error?.message || "Error executing record action"
            );
            console.error("RecordProducerActionError:", error);
            return false;
        }
    };

    return {
        runActionScript
    }
}

export default useRecordProducerActionCompiler;

const handleSuccessNavigation = ({
    action,
    tableName,
    navigate,
    currentValues,
    returnValue,
}: {
    action: SysRecord;
    tableName: string;
    navigate: NavigateFunction;
    currentValues: Record<string, any>;
    returnValue: unknown;
}) => {
    if (returnValue === false) {
        return;
    }

    const navigation = normalizeSuccessNavigation(action.success_navigation);

    if (!navigation || navigation === "custom" || navigation === "stay") {
        return;
    }

    const fallback = normalizeSuccessNavigationFallback(action.success_navigation_fallback);
    const recordId = getRecordId(returnValue) || getRecordId(currentValues);

    if (navigation === "return_to") {
        const fallbackPath = buildFallbackPath(fallback, tableName, recordId);
        navigateToReturnTarget(navigate, { fallback: fallbackPath || undefined });
        return;
    }

    if (navigation === "current_record" && recordId) {
        navigate(`/home/workspace/${tableName}/record/${recordId}`);
        return;
    }

    if (navigation === "table_list") {
        navigate(`/home/workspace/${tableName}/list`);
    }
};

const normalizeSuccessNavigation = (value: unknown): SuccessNavigation | null => {
    const normalizedValue = typeof value === "string" ? value.trim() : "";
    if (["custom", "stay", "return_to", "current_record", "table_list"].includes(normalizedValue)) {
        return normalizedValue as SuccessNavigation;
    }

    return null;
};

const normalizeSuccessNavigationFallback = (value: unknown): SuccessNavigationFallback => {
    const normalizedValue = typeof value === "string" ? value.trim() : "";
    if (["none", "current_record", "table_list"].includes(normalizedValue)) {
        return normalizedValue as SuccessNavigationFallback;
    }

    return "none";
};

const buildFallbackPath = (
    fallback: SuccessNavigationFallback,
    tableName: string,
    recordId?: string | null
) => {
    if (fallback === "current_record" && recordId) {
        return `/home/workspace/${tableName}/record/${recordId}`;
    }

    if (fallback === "table_list") {
        return `/home/workspace/${tableName}/list`;
    }

    return null;
};

const getRecordId = (value: unknown): string | null => {
    if (!value || typeof value !== "object") return null;

    const record = value as Record<string, any>;
    const data = record.data && typeof record.data === "object" ? record.data : null;
    const id = record.id ?? data?.id;

    return id ? String(id) : null;
};

const buildCurrentPayload = (
    values: Record<string, any>,
    recordProducer: ContextType<typeof RecordProducerContext>
) => {
    const sanitizedValues = sanitizePasswordPayloadValues(values, recordProducer?.columns ?? []);
    for (const column of recordProducer?.columns ?? []) {
        if (["CODE", "JSON"].includes(column.column_type) && column.name in sanitizedValues) {
            sanitizedValues[column.name] = normalizeCodePayload(sanitizedValues[column.name]);
        }
    }

    if (recordProducer?.mode !== "create" || !recordProducer.attachmentDraftToken) {
        return sanitizedValues;
    }

    return {
        ...sanitizedValues,
        _attachment_draft_token: recordProducer.attachmentDraftToken,
    };
};
